import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { SlashCommandGroup, SlashCommandItem } from './slash-command'

// `/` opens a word (start or after whitespace); query runs to the caret.
const INPUT_SLASH_RE = /(?:^|\s)\/([^\s/]*)$/

/** The plain-text token a field inserts — from a string content, or the
 *  `insert`/`value` attr of a templateVariable node (shared with the editor). */
function tokenOf(item: SlashCommandItem): string {
  if (typeof item.content === 'string') return item.content
  const attrs = (
    item.content as { attrs?: { insert?: string; value?: string } }
  ).attrs
  return attrs?.insert ?? attrs?.value ?? ''
}

interface InputTrigger {
  query: string
  from: number
  to: number
}

export interface InputSlashController {
  open: boolean
  query: string
  rect: { bottom: number; left: number } | null
  onSelect: (item: SlashCommandItem) => void
  onDismiss: () => void
  syncCaret: () => void
}

/** Slash (`/`) quick-insert for a plain `<input>`: detects the trigger from the
 *  value + caret, anchors the shared menu under the field, and splices the
 *  selected field's token (plus a trailing space) into the value. */
export function useInputSlash({
  inputRef,
  value,
  groups,
  onChange,
  onUse,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>
  value: string
  groups: SlashCommandGroup[]
  onChange: (next: string) => void
  onUse?: (item: SlashCommandItem) => void
}): InputSlashController {
  const enabled = groups.length > 0
  const [caret, setCaret] = useState(0)

  const syncCaret = useCallback(() => {
    const el = inputRef.current
    if (el) setCaret(el.selectionStart ?? el.value.length)
  }, [inputRef])

  const trigger = useMemo<InputTrigger | null>(() => {
    if (!enabled) return null
    const match = INPUT_SLASH_RE.exec(value.slice(0, caret))
    if (!match) return null
    const query = match[1]
    return { query, from: caret - (query.length + 1), to: caret }
  }, [enabled, value, caret])

  const triggerFrom = trigger ? trigger.from : null
  const [dismissedFrom, setDismissedFrom] = useState<number | null>(null)
  const open = trigger !== null && triggerFrom !== dismissedFrom

  useEffect(() => {
    if (triggerFrom === null && dismissedFrom !== null) setDismissedFrom(null)
  }, [triggerFrom, dismissedFrom])

  const rect = useMemo(() => {
    if (!open) return null
    const el = inputRef.current
    if (!el) return null
    const r = el.getBoundingClientRect()
    return { bottom: r.bottom, left: r.left }
  }, [open, inputRef, value, caret])

  const triggerRef = useRef(trigger)
  triggerRef.current = trigger

  const onSelect = useCallback(
    (item: SlashCommandItem) => {
      const tr = triggerRef.current
      if (!tr) return
      const token = tokenOf(item)
      const next = `${value.slice(0, tr.from)}${token} ${value.slice(tr.to)}`
      const caretPos = tr.from + token.length + 1
      onChange(next)
      onUse?.(item)
      requestAnimationFrame(() => {
        const el = inputRef.current
        if (!el) return
        el.focus()
        el.setSelectionRange(caretPos, caretPos)
        setCaret(caretPos)
      })
    },
    [value, onChange, onUse, inputRef],
  )

  const onDismiss = useCallback(() => {
    const tr = triggerRef.current
    if (tr) setDismissedFrom(tr.from)
  }, [])

  return {
    open,
    query: trigger ? trigger.query : '',
    rect,
    onSelect,
    onDismiss,
    syncCaret,
  }
}
